import React, { useState, useEffect, useCallback } from 'react';
import { Link2, Heart, Eye, Users, Plus, X, Search, ExternalLink, Copy, Check, Star, Trash2, ArrowLeft, BadgeCheck, ChevronDown, CreditCard as Edit3 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContextSupabase';

interface Client {
  id: string;
  brand_name: string;
  public_slug: string | null;
  page_views: number;
}

interface Influencer {
  id: string;
  username: string;
  display_name: string | null;
  profile_image_url: string | null;
  followers_count: number;
  engagement_rate: number;
  category: string | null;
  is_exclusive: boolean;
}

interface RosterSelection {
  id: string;
  client_id: string;
  influencer_id: string;
  note: string;
  position: number;
  influencer?: Influencer;
}

interface RosterLike {
  influencer_id: string;
  count: number;
  influencer?: Influencer;
}

interface ClientWithStats extends Client {
  selections: RosterSelection[];
  likesCount: number;
  topLiked: string[];
  likedInfluencers: RosterLike[];
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

export default function CastingInterne() {
  const { userProfile } = useAuth();
  const [clients, setClients] = useState<ClientWithStats[]>([]);
  const [allInfluencers, setAllInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  // Vue détaillée d'une marque
  const [activeClient, setActiveClient] = useState<ClientWithStats | null>(null);

  // Slug editor
  const [editingSlug, setEditingSlug] = useState<{ clientId: string; slug: string } | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);

  // Note editor in detail view
  const [editingNote, setEditingNote] = useState<{ selId: string; note: string } | null>(null);

  // Search in detail view
  const [searchInfluencer, setSearchInfluencer] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const agencyId = userProfile?.agency_id;

  const load = useCallback(async (): Promise<ClientWithStats[]> => {
    if (!agencyId) return [];
    setLoading(true);
    try {
      const { data: orgInfs } = await supabase
        .from('organization_influencers')
        .select('influencer_id')
        .limit(500);

      const orgIds = (orgInfs || []).map((o: any) => o.influencer_id);

      let influencersData: Influencer[] = [];
      if (orgIds.length > 0) {
        const batches: Influencer[][] = [];
        for (let i = 0; i < orgIds.length; i += 100) {
          const batch = orgIds.slice(i, i + 100);
          const { data } = await supabase
            .from('influencers')
            .select('id, username, display_name, profile_image_url, followers_count, engagement_rate, category, is_exclusive')
            .in('id', batch)
            .order('followers_count', { ascending: false });
          if (data) batches.push(data);
        }
        influencersData = batches.flat();
      }

      const [{ data: clientsData }, { data: selectionsData }, { data: likesData }] = await Promise.all([
        supabase.from('clients').select('id, brand_name, public_slug, page_views').eq('agency_id', agencyId).order('brand_name'),
        supabase.from('roster_selections').select('id, client_id, influencer_id, note, position').eq('agency_id', agencyId),
        supabase.from('roster_likes').select('influencer_id, client_id'),
      ]);

      const infMap = new Map(influencersData.map(i => [i.id, i]));

      // Load any liked influencers not already in the org map
      const likedIds = [...new Set((likesData || []).map((l: any) => l.influencer_id))];
      const missingIds = likedIds.filter(id => !infMap.has(id));
      if (missingIds.length > 0) {
        const { data: missingInfs } = await supabase
          .from('influencers')
          .select('id, username, display_name, profile_image_url, followers_count, engagement_rate, category, is_exclusive')
          .in('id', missingIds);
        for (const inf of (missingInfs || [])) infMap.set(inf.id, inf);
      }

      const likesByClient: Record<string, Record<string, number>> = {};
      for (const like of (likesData || [])) {
        if (!likesByClient[like.client_id]) likesByClient[like.client_id] = {};
        likesByClient[like.client_id][like.influencer_id] = (likesByClient[like.client_id][like.influencer_id] || 0) + 1;
      }

      const enriched: ClientWithStats[] = (clientsData || []).map(c => {
        const sels = (selectionsData || [])
          .filter(s => s.client_id === c.id)
          .sort((a, b) => a.position - b.position)
          .map(s => ({ ...s, influencer: infMap.get(s.influencer_id) }));

        const clientLikes = likesByClient[c.id] || {};
        const sortedLikes = Object.entries(clientLikes).sort((a, b) => b[1] - a[1]);
        const topLiked = sortedLikes.slice(0, 3).map(([id]) => infMap.get(id)?.username || id);
        const likedInfluencers: RosterLike[] = sortedLikes.map(([id, count]) => ({
          influencer_id: id,
          count,
          influencer: infMap.get(id),
        }));

        return {
          ...c,
          page_views: c.page_views || 0,
          selections: sels,
          likesCount: Object.values(clientLikes).reduce((s, n) => s + n, 0),
          topLiked,
          likedInfluencers,
        };
      });

      setClients(enriched);
      setAllInfluencers(influencersData);
      return enriched;
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => { load(); }, [load]); // activeClient sync handled inside mutations

  const copyLink = (slug: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/r/${slug}`);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const reloadAndSync = async (clientId?: string) => {
    const enriched = await load();
    if (clientId) {
      const updated = enriched.find(c => c.id === clientId);
      if (updated) setActiveClient(updated);
    }
  };

  const generateSlug = async (client: Client) => {
    const base = slugify(client.brand_name);
    await supabase.from('clients').update({ public_slug: base }).eq('id', client.id);
    await reloadAndSync(client.id);
  };

  const saveSlug = async () => {
    if (!editingSlug) return;
    const slug = slugify(editingSlug.slug);
    if (!slug) { setSlugError('Slug invalide'); return; }
    const { error } = await supabase.from('clients').update({ public_slug: slug }).eq('id', editingSlug.clientId);
    if (error?.code === '23505') { setSlugError('Ce slug est déjà utilisé'); return; }
    setEditingSlug(null);
    setSlugError(null);
    await reloadAndSync(editingSlug.clientId);
  };

  const toggleSelection = async (client: ClientWithStats, influencer: Influencer) => {
    // Lire les sélections actuelles depuis la DB pour éviter les stale closures
    const { data: currentSels } = await supabase
      .from('roster_selections')
      .select('id, influencer_id')
      .eq('client_id', client.id)
      .eq('influencer_id', influencer.id);

    if (currentSels && currentSels.length > 0) {
      await supabase.from('roster_selections').delete().eq('id', currentSels[0].id);
    } else {
      const { data: allSels } = await supabase
        .from('roster_selections')
        .select('id')
        .eq('client_id', client.id);
      await supabase.from('roster_selections').insert({
        agency_id: agencyId,
        client_id: client.id,
        influencer_id: influencer.id,
        note: '',
        position: (allSels?.length || 0),
      });
    }
    await reloadAndSync(client.id);
  };

  const saveNote = async () => {
    if (!editingNote) return;
    await supabase.from('roster_selections').update({ note: editingNote.note }).eq('id', editingNote.selId);
    setEditingNote(null);
    if (activeClient) await reloadAndSync(activeClient.id);
  };

  const categories = [...new Set(allInfluencers.map(i => i.category).filter(Boolean))].sort() as string[];

  const filteredInfluencers = allInfluencers.filter(i => {
    if (filterCategory && i.category !== filterCategory) return false;
    if (!searchInfluencer) return true;
    const q = searchInfluencer.toLowerCase();
    return (i.username || '').toLowerCase().includes(q) || (i.display_name || '').toLowerCase().includes(q);
  });

  if (loading && clients.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/10 border-t-white/60 rounded-full animate-spin" />
      </div>
    );
  }

  // ── Vue détaillée d'une marque ───────────────────────────────────────────────
  if (activeClient) {
    const selectedIds = new Set(activeClient.selections.map(s => s.influencer_id));

    return (
      <div className="min-h-screen">
        <div className="max-w-6xl mx-auto px-4 py-6">

          {/* Breadcrumb */}
          <button
            onClick={() => { setActiveClient(null); setSearchInfluencer(''); setFilterCategory(''); }}
            className="flex items-center gap-2 text-sm text-white/50 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Casting interne
          </button>

          {/* Header marque */}
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                <span className="text-base font-bold text-white/60">{activeClient.brand_name[0]?.toUpperCase()}</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{activeClient.brand_name}</h1>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-white/40">
                  <span className="flex items-center gap-1"><Star className="w-3 h-3" />{activeClient.selections.length} sélectionné{activeClient.selections.length > 1 ? 's' : ''}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{activeClient.page_views} vues</span>
                  <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{activeClient.likesCount} likes</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {activeClient.public_slug ? (
                <>
                  <a
                    href={`/r/${activeClient.public_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white/60 bg-white/[0.08] border border-white/[0.08] hover:bg-white/[0.14] rounded-xl transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Voir la page
                  </a>
                  <button
                    onClick={() => copyLink(activeClient.public_slug!)}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-black bg-white hover:bg-white/90 rounded-xl transition-colors"
                  >
                    {copiedSlug === activeClient.public_slug ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copiedSlug === activeClient.public_slug ? 'Copié !' : 'Copier le lien'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => generateSlug(activeClient)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-black bg-white hover:bg-white/90 rounded-xl transition-colors"
                >
                  <Link2 className="w-3.5 h-3.5" />
                  Activer le lien public
                </button>
              )}
            </div>
          </div>

          {/* Profils likés par la marque */}
          {activeClient.likedInfluencers.length > 0 && (
            <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-5 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-4 h-4 text-rose-400 fill-rose-400" />
                <span className="text-sm font-semibold text-white">Profils likés par {activeClient.brand_name}</span>
                <span className="text-xs text-white/40 ml-auto">{activeClient.likedInfluencers.length} profil{activeClient.likedInfluencers.length > 1 ? 's' : ''}</span>
              </div>
              <div className="space-y-2">
                {activeClient.likedInfluencers.map(({ influencer_id, count, influencer }) => (
                  <div key={influencer_id} className="flex items-center gap-3 p-2.5 bg-rose-500/10 rounded-xl">
                    <img
                      src={influencer?.profile_image_url || `https://ui-avatars.com/api/?name=${influencer?.username || '?'}&background=fce7f3&color=be185d`}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-white">
                          {influencer ? `@${influencer.username}` : influencer_id}
                        </span>
                        {influencer && <span className="text-xs text-white/40">{formatNumber(influencer.followers_count)}</span>}
                        {influencer?.is_exclusive && <span className="text-xs font-medium px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded-md">Exclu</span>}
                      </div>
                      {influencer?.category && <p className="text-xs text-white/40 mt-0.5">{influencer.category}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="flex items-center gap-1 text-xs font-semibold text-rose-400 bg-rose-500/20 px-2 py-0.5 rounded-full">
                        <Heart className="w-3 h-3 fill-rose-400" /> {count}
                      </span>
                      {influencer && !selectedIds.has(influencer_id) && (
                        <button
                          onClick={() => toggleSelection(activeClient, influencer)}
                          className="text-xs text-white/40 hover:text-amber-400 hover:bg-amber-500/10 px-2 py-0.5 rounded-full border border-white/[0.08] hover:border-amber-500/30 transition-colors"
                        >
                          + Sélectionner
                        </button>
                      )}
                      {influencer && selectedIds.has(influencer_id) && (
                        <span className="text-xs text-amber-400 font-medium flex items-center gap-1">
                          <Star className="w-3 h-3" /> Sélectionné
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sélection actuelle */}
          {activeClient.selections.length > 0 && (
            <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-5 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-white">Notre sélection pour {activeClient.brand_name}</span>
                <span className="text-xs text-white/40 ml-auto">{activeClient.selections.length} créateur{activeClient.selections.length > 1 ? 's' : ''}</span>
              </div>
              <div className="space-y-2">
                {activeClient.selections.map(sel => sel.influencer && (
                  <div key={sel.id} className="flex items-center gap-3 p-2.5 bg-amber-500/10 rounded-xl">
                    <img
                      src={sel.influencer.profile_image_url || `https://ui-avatars.com/api/?name=${sel.influencer.username}&background=fef3c7&color=92400e`}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-white">@{sel.influencer.username}</span>
                        <span className="text-xs text-white/40">{formatNumber(sel.influencer.followers_count)}</span>
                        {sel.influencer.is_exclusive && <span className="text-xs font-medium px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded-md">Exclu</span>}
                      </div>
                      {editingNote?.selId === sel.id ? (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            value={editingNote.note}
                            onChange={e => setEditingNote({ ...editingNote, note: e.target.value })}
                            className="flex-1 px-2 py-1 bg-white/[0.06] border border-white/[0.08] rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                            placeholder="Pourquoi ce profil pour cette marque ?"
                            autoFocus
                            onKeyDown={e => e.key === 'Enter' && saveNote()}
                          />
                          <button onClick={saveNote} className="px-2 py-1 bg-white text-black text-xs rounded-lg font-semibold">OK</button>
                          <button onClick={() => setEditingNote(null)} className="px-2 py-1 bg-white/[0.08] text-white/60 text-xs rounded-lg">×</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingNote({ selId: sel.id, note: sel.note || '' })}
                          className="text-left text-xs text-white/40 hover:text-white/70 mt-0.5 flex items-center gap-1 transition-colors"
                        >
                          <Edit3 className="w-3 h-3" />
                          {sel.note || <span className="italic">Ajouter une note…</span>}
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => toggleSelection(activeClient, sel.influencer!)}
                      className="p-1.5 text-white/30 hover:text-red-400 transition-colors flex-shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grille de tous les créateurs */}
          <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-white">Tous les créateurs</h2>
                <p className="text-xs text-white/40 mt-0.5">Sélectionnez ceux à mettre en avant pour {activeClient.brand_name}</p>
              </div>
              <span className="text-xs text-white/40">{selectedIds.size} / {allInfluencers.length}</span>
            </div>

            {/* Filtres */}
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  value={searchInfluencer}
                  onChange={e => setSearchInfluencer(e.target.value)}
                  placeholder="Rechercher un créateur…"
                  className="w-full pl-9 pr-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                />
              </div>
              {categories.length > 0 && (
                <div className="relative">
                  <select
                    value={filterCategory}
                    onChange={e => setFilterCategory(e.target.value)}
                    className="appearance-none pl-3 pr-8 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-sm text-white/60 focus:outline-none focus:ring-1 focus:ring-white/20 cursor-pointer"
                  >
                    <option value="">Toutes catégories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
                </div>
              )}
              {selectedIds.size > 0 && (
                <button
                  onClick={() => setFilterCategory('__selected__')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${filterCategory === '__selected__' ? 'bg-amber-500/20 border-amber-500/30 text-amber-400' : 'bg-white/[0.08] border-white/[0.08] text-white/60 hover:border-amber-500/30'}`}
                >
                  <Star className="w-3.5 h-3.5" />
                  Sélectionnés ({selectedIds.size})
                </button>
              )}
            </div>

            {/* Grille */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {(filterCategory === '__selected__'
                ? allInfluencers.filter(i => selectedIds.has(i.id))
                : filteredInfluencers
              ).map(inf => {
                const isSelected = selectedIds.has(inf.id);
                return (
                  <button
                    key={inf.id}
                    onClick={() => toggleSelection(activeClient, inf)}
                    className={`relative rounded-2xl overflow-hidden border-2 transition-all text-left group ${
                      isSelected
                        ? 'border-amber-400 shadow-md shadow-amber-500/20'
                        : 'border-white/[0.06] hover:border-white/20'
                    }`}
                  >
                    {/* Image */}
                    <div className="aspect-square relative overflow-hidden bg-white/[0.06]">
                      <img
                        src={inf.profile_image_url || `https://ui-avatars.com/api/?name=${inf.username}&background=e2e8f0&color=475569&size=200`}
                        alt={inf.username}
                        className="w-full h-full object-cover"
                      />
                      {/* Overlay sélectionné */}
                      {isSelected && (
                        <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center shadow-lg">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                      {/* Overlay hover */}
                      {!isSelected && (
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow">
                            <Plus className="w-4 h-4 text-gray-700" />
                          </div>
                        </div>
                      )}
                      {inf.is_exclusive && (
                        <div className="absolute top-1.5 right-1.5">
                          <div className="w-5 h-5 rounded-full bg-amber-500/90 flex items-center justify-center">
                            <BadgeCheck className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className={`px-2 py-2 ${isSelected ? 'bg-amber-500/10' : 'bg-white/[0.04]'}`}>
                      <p className="font-semibold text-xs text-white truncate">@{inf.username}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[11px] text-white/40">{formatNumber(inf.followers_count)}</span>
                        <span className="text-[11px] text-emerald-400 font-medium">{inf.engagement_rate?.toFixed(1)}%</span>
                      </div>
                    </div>
                  </button>
                );
              })}

              {filteredInfluencers.length === 0 && filterCategory !== '__selected__' && (
                <div className="col-span-full text-center py-12 text-white/30 text-sm">
                  Aucun créateur trouvé
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Vue liste des marques ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Casting interne</h1>
          <p className="text-white/50 mt-1 text-sm">
            Partagez votre roster avec vos marques — une page personnalisée par client.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Marques avec lien actif', value: clients.filter(c => c.public_slug).length, icon: Link2, color: 'text-blue-400 bg-blue-500/20' },
            { label: 'Vues totales', value: clients.reduce((s, c) => s + c.page_views, 0), icon: Eye, color: 'text-emerald-400 bg-emerald-500/20' },
            { label: 'Likes reçus', value: clients.reduce((s, c) => s + c.likesCount, 0), icon: Heart, color: 'text-rose-400 bg-rose-500/20' },
          ].map(stat => (
            <div key={stat.label} className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-5 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-xs text-white/50">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Clients list */}
        <div className="space-y-2">
          {clients.length === 0 && (
            <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-12 text-center">
              <Users className="w-10 h-10 text-white/30 mx-auto mb-3" />
              <p className="text-white/50">Aucun client trouvé. Créez d'abord des clients.</p>
            </div>
          )}

          {clients.map(client => (
            <div key={client.id} className="bg-white/[0.04] rounded-2xl border border-white/[0.08] hover:bg-white/[0.06] transition-colors">
              <div
                className="flex items-center gap-4 px-5 py-4 cursor-pointer"
                onClick={() => setActiveClient(client)}
              >
                <div className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-white/60">{client.brand_name[0]?.toUpperCase()}</span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white">{client.brand_name}</span>
                    {client.public_slug && (
                      <span className="text-xs text-white/30 font-mono hidden sm:inline">/r/{client.public_slug}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-white/40">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{client.page_views} vues</span>
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{client.likesCount} likes</span>
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      {client.selections.length > 0
                        ? <span className="text-amber-400 font-medium">{client.selections.length} sélectionné{client.selections.length > 1 ? 's' : ''}</span>
                        : '0 sélectionné'
                      }
                    </span>
                    {client.topLiked.length > 0 && (
                      <span className="text-rose-400 hidden sm:inline">Top : {client.topLiked.join(', ')}</span>
                    )}
                  </div>
                </div>

                {/* Actions rapides */}
                <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  {client.public_slug ? (
                    <>
                      <a
                        href={`/r/${client.public_slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-xl transition-colors"
                        title="Voir la page publique"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => copyLink(client.public_slug!)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white/60 bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-colors"
                      >
                        {copiedSlug === client.public_slug ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        {copiedSlug === client.public_slug ? 'Copié' : 'Lien'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => generateSlug(client)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-black bg-white hover:bg-white/90 rounded-xl transition-colors"
                    >
                      <Link2 className="w-3.5 h-3.5" />
                      Activer
                    </button>
                  )}
                </div>
              </div>

              {/* Slug editor inline */}
              {editingSlug?.clientId === client.id && (
                <div className="px-5 pb-4 flex items-center gap-2 border-t border-white/[0.06] pt-3">
                  <span className="text-sm text-white/40 text-nowrap">{window.location.origin}/r/</span>
                  <input
                    value={editingSlug.slug}
                    onChange={e => setEditingSlug({ ...editingSlug, slug: e.target.value })}
                    className="flex-1 px-3 py-1.5 bg-white/[0.06] border border-white/[0.08] rounded-lg text-sm font-mono text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                    onKeyDown={e => e.key === 'Enter' && saveSlug()}
                    autoFocus
                  />
                  <button onClick={saveSlug} className="px-3 py-1.5 bg-white text-black text-xs rounded-lg hover:bg-white/90 font-semibold">Enregistrer</button>
                  <button onClick={() => { setEditingSlug(null); setSlugError(null); }} className="px-3 py-1.5 bg-white/[0.08] text-white/60 text-xs rounded-lg">Annuler</button>
                  {slugError && <span className="text-xs text-red-400">{slugError}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
